import {Portal as LumaPortal} from '@headlessui/react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    activarOperadorPOS,
    fetchOperadoresPOS,
    OperadorPOS,
    OperadorPOSGrant,
} from '../../services/pos/sesionPOSService.ts';
import { useModalLifecycle } from '../../hooks/useModalLifecycle.ts';
import './OperadorPinDialog.css';

interface OperadorPinDialogProps {
    open: boolean;
    /** Caja EXPLÍCITA sobre la que se emite el grant; sin id no se abre. */
    sesionId: number | null;
    onClose: () => void;
    onGrant: (grant: OperadorPOSGrant) => void;
}

const FOCUSABLE_SELECTOR = [
    'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
    'textarea:not([disabled])', 'a[href]', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Devuelve el foco a ``element`` solo si sigue en el documento y es enfocable. */
const restoreFocus = (element: HTMLElement | null) => {
    if (!element || !element.isConnected || typeof element.focus !== 'function') return;
    try { element.focus(); } catch { /* elemento ya no enfocable: se deja donde esté */ }
};

const PIN_PATTERN = /^\d{3,6}$/;

/**
 * Identificación del operador de caja por PIN fuera del navbar del POS
 * (kardex, pagos administrativos, edición de citas). Listar y activar
 * comparten UNA generación: abrir, cerrar, cambiar de caja o desmontar la
 * incrementan, así que ninguna respuesta tardía puede pisar la lista ni
 * instalar un grant de otra apertura u otra caja.
 *
 * Activar es distinto de cargar el listado: mientras una activación está en
 * vuelo la selección de operador, el PIN y el envío quedan bloqueados (el
 * listado sigue visible) y la respuesta solo se aplica si el operador
 * seleccionado sigue siendo el solicitado; así nunca se instala el grant ni se
 * muestra el error de un operador distinto al que se ve en pantalla.
 *
 * Identidad del diálogo = apertura + caja, fijada DE FORMA SÍNCRONA en
 * render por ``useModalLifecycle``: al detectar un cambio (abrir, cerrar,
 * cambiar de caja) se invalida la generación de peticiones y se apagan los
 * handlers sin tocar estado; el reset repone el estado visual y la identidad
 * nueva solo se habilita en el commit que ya muestra ese estado limpio. Así
 * ninguna respuesta que llegue entre el commit y los efectos, ni un envío en
 * esa frontera, puede usar la selección o el PIN de la caja anterior.
 *
 * Cerrar es terminal e idempotente: apaga el diálogo ANTES de avisar al padre
 * y avisa una sola vez por apertura. Si el padre deja el diálogo montado,
 * cerrar de nuevo (botón, backdrop, Escape), elegir operador, escribir el PIN
 * y enviar son inertes; solo una apertura realmente nueva reinicia.
 *
 * Modal accesible: al abrir guarda el elemento enfocado (opener) y mueve el
 * foco dentro (primer operador habilitado, o cerrar mientras carga/si no hay
 * ninguno); si los nodos enfocados desaparecen (cambio de caja, lista nueva)
 * el foco se recoloca en cerrar/panel en el mismo commit; Escape cierra;
 * Tab/Shift+Tab quedan confinados; al cerrar o desmontar devuelve el foco al
 * opener si sigue en el documento.
 */
export const OperadorPinDialog: React.FC<OperadorPinDialogProps> = ({ open, sesionId, onClose, onGrant }) => {
    const [operators, setOperators] = useState<OperadorPOS[]>([]);
    const [selected, setSelected] = useState<OperadorPOS | null>(null);
    const [pin, setPin] = useState('');
    const [loadingList, setLoadingList] = useState(false);
    const [activating, setActivating] = useState(false);
    const [error, setError] = useState('');
    // Espejo síncrono de ``selected``: la respuesta de una activación se
    // valida contra el operador vigente en ese instante.
    const selectedRef = useRef<OperadorPOS | null>(null);
    const panelRef = useRef<HTMLFormElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Identidad (apertura + caja), generación, bloqueo de activación y cierre
    // único. El reset visual corre en layout: el estado de la caja anterior no
    // llega a pintarse y la identidad nueva solo se habilita ya repuesta.
    const lifecycle = useModalLifecycle({
        identity: open && sesionId !== null ? sesionId : null,
        onClose,
        onReset: () => {
            selectedRef.current = null;
            setOperators([]); setSelected(null); setPin(''); setError(''); setActivating(false); setLoadingList(false);
        },
    });
    const { identityKey, rendered, close } = lifecycle;

    // Carga de operadores de ESTA identidad (mismo commit que el reset).
    useLayoutEffect(() => {
        if (!rendered || sesionId === null) return;
        const requestId = lifecycle.invalidate();
        setLoadingList(true);
        // Lista y fin de carga en la MISMA actualización (sin ``finally`` aparte):
        // el grid aparece en el mismo render en que llegan los operadores y el
        // efecto de foco inicial los encuentra.
        fetchOperadoresPOS(sesionId)
            .then(list => { if (lifecycle.isLive(requestId)) { setOperators(list); setLoadingList(false); } })
            .catch(() => { if (lifecycle.isLive(requestId)) { setError('No se pudieron cargar los operadores autorizados.'); setLoadingList(false); } });
        // La clave de identidad ya codifica apertura y caja; el resto son refs/estables.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identityKey]);

    // Opener + foco inicial + restauración. Depende solo de ``rendered``:
    // cambiar de caja con el diálogo abierto conserva el opener original.
    // Layout effect: captura el opener ANTES de que el guardián de abajo pueda
    // mover el foco dentro del panel.
    useLayoutEffect(() => {
        if (!rendered) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const panel = panelRef.current;
        if (panel && !(opener && panel.contains(opener))) {
            (closeButtonRef.current ?? panel).focus();
        }
        return () => { restoreFocus(opener); };
    }, [rendered]);

    // Guardián de foco: tras CADA commit con el diálogo abierto, si el elemento
    // enfocado ya no está dentro (p. ej. desaparecieron los operadores o el
    // campo del PIN al cambiar de caja y el foco cayó al body) se recoloca de
    // inmediato en cerrar (o el panel). Sin deps: mismo commit que quita los nodos.
    useLayoutEffect(() => {
        if (!rendered) return;
        const panel = panelRef.current;
        if (!panel) return;
        const active = document.activeElement;
        if (active && panel.contains(active)) return;
        (closeButtonRef.current ?? panel).focus();
    });

    // Al llegar la lista, foco al primer operador habilitado salvo que el
    // usuario ya haya movido el foco a otro control del diálogo.
    useEffect(() => {
        if (!rendered || operators.length === 0) return;
        const panel = panelRef.current;
        if (!panel) return;
        const active = document.activeElement;
        if (active && panel.contains(active) && active !== closeButtonRef.current) return;
        const first = panel.querySelector<HTMLElement>('.opd-operator:not([disabled])');
        (first ?? closeButtonRef.current ?? panel).focus();
    }, [rendered, operators]);

    // Escape cierra; Tab/Shift+Tab ciclan dentro del diálogo y recuperan el
    // foco si se escapó (p. ej. al desmontarse el elemento enfocado).
    useEffect(() => {
        if (!rendered) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') { event.preventDefault(); close(); return; }
            if (event.key !== 'Tab') return;
            const panel = panelRef.current;
            if (!panel) return;
            const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            if (items.length === 0) { event.preventDefault(); panel.focus(); return; }
            const first = items[0];
            const last = items[items.length - 1];
            if (!active || !panel.contains(active)) { event.preventDefault(); first.focus(); return; }
            if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [rendered, close]);

    // Los handlers exigen identidad activa (reset en pantalla, sin cierre,
    // misma caja) y ninguna activación en curso (bloqueo síncrono).
    const isInteractive = () => lifecycle.canWrite();

    const chooseOperator = (operator: OperadorPOS) => {
        if (!isInteractive()) return;
        selectedRef.current = operator;
        setSelected(operator); setPin(''); setError('');
    };

    const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isInteractive()) return;
        setPin(event.target.value.replace(/\D/g, '').slice(0, 6));
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!isInteractive()) return;
        if (sesionId === null || !selected || selectedRef.current?.id !== selected.id || !PIN_PATTERN.test(pin)) {
            setError('Ingresa un PIN numérico de 3 a 6 dígitos.');
            return;
        }
        // Bloqueo síncrono (un Enter repetido no lanza otra activación) y
        // generación de la identidad SOLICITADA: un cambio de apertura/caja o
        // el cierre la invalidan ya en render, y el operador se revalida contra
        // la selección vigente antes de entregar el grant o mostrar el error.
        const requestId = lifecycle.beginWrite();
        if (requestId === null) return;
        const requestedSession = sesionId;
        const requestedOperator = selected.id;
        const stillRequested = () => lifecycle.isLive(requestId) && selectedRef.current?.id === requestedOperator;
        setActivating(true); setError('');
        try {
            const grant = await activarOperadorPOS(requestedSession, requestedOperator, pin);
            if (!stillRequested()) return;
            onGrant(grant);
            close();
        } catch (err: any) {
            if (!stillRequested()) return;
            setError(err?.response?.data?.detail || err?.response?.data?.error || 'PIN incorrecto o acceso no autorizado.');
        } finally {
            // Tras ``close`` la generación ya no está viva: el diálogo queda
            // bloqueado hasta la próxima apertura (el reset repone el estado).
            if (lifecycle.isLive(requestId)) { lifecycle.endWrite(requestId); setActivating(false); }
        }
    };

    if (!rendered) return null;

    // Layout y media queries en OperadorPinDialog.css: aquí solo estructura,
    // clases y estado (nada de anchos/paddings fijos inline).
    return <LumaPortal>{<div role="presentation" className="opd-overlay" data-testid="opd-overlay" onClick={close}>
            <form ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="opd-title" className="opd-panel" onSubmit={submit} onClick={e => e.stopPropagation()}>
                <div className="opd-header">
                    <div className="opd-header-text" data-testid="opd-header-text">
                        <h3 id="opd-title" className="opd-title">Identificar operador de caja</h3>
                        <p className="opd-subtitle">Elige tu nombre e ingresa tu PIN personal.</p>
                    </div>
                    <button ref={closeButtonRef} type="button" aria-label="Cerrar identificación de operador" className="opd-close" onClick={close}>×</button>
                </div>
                {loadingList ? <p role="status">Cargando operadores…</p> : (
                    <div className="opd-grid" data-testid="opd-grid">
                        {operators.map(operator => (
                            <button key={operator.id} type="button" disabled={!operator.pin_configurado || activating} aria-label={operator.nombre}
                                aria-pressed={selected?.id === operator.id}
                                className={`opd-operator${selected?.id === operator.id ? ' is-selected' : ''}`}
                                onClick={() => chooseOperator(operator)}>
                                <span className="opd-operator-name">{operator.nombre}</span>
                                {!operator.pin_configurado && <span className="opd-operator-hint">PIN no configurado</span>}
                            </button>
                        ))}
                    </div>
                )}
                {selected && (
                    <div>
                        <label htmlFor="opd-pin" className="opd-pin-label">PIN de caja</label>
                        {/* readOnly (no disabled) durante la activación: conserva el foco
                            y el flujo de teclado; el cambio se ignora también en el handler. */}
                        <input id="opd-pin" type="password" inputMode="numeric" autoComplete="off" pattern="[0-9]{3,6}" minLength={3} maxLength={6} value={pin}
                            readOnly={activating} aria-busy={activating} onChange={handlePinChange} autoFocus className="opd-pin" />
                    </div>
                )}
                {error && <p role="alert" className="opd-error">{error}</p>}
                {selected && (
                    <button type="submit" className="opd-submit" disabled={activating || !PIN_PATTERN.test(pin)}>
                        Ingresar como {selected.nombre}
                    </button>
                )}
            </form>
        </div>}</LumaPortal>;
};

interface OperadorGrantState {
    /** Caja a la que pertenecen ``grant`` y ``dialogOpen``. */
    sesionId: number | null;
    grant: OperadorPOSGrant | null;
    dialogOpen: boolean;
}

const EMPTY_GRANT_STATE = (sesionId: number | null): OperadorGrantState => ({ sesionId, grant: null, dialogOpen: false });

/**
 * Grant del operador ligado a UNA caja: cambiar de caja (o perderla) lo
 * descarta. El estado guarda la caja que lo originó y se lee DERIVADO de la
 * caja actual: en el mismo render en que cambia ``sesionId`` el grant y el
 * diálogo dejan de existir, sin ventana entre render y efecto (ni un
 * ``token`` de otra caja visible ni un diálogo que se cierre tarde). Expone
 * lo necesario para pintar el estado y abrir el diálogo.
 */
export const useOperadorGrant = (sesionId: number | null) => {
    const [state, setState] = useState<OperadorGrantState>(() => EMPTY_GRANT_STATE(sesionId));
    const current = state.sesionId === sesionId ? state : EMPTY_GRANT_STATE(sesionId);
    const openDialog = useCallback(() => {
        if (sesionId === null) return;
        setState(prev => ({ sesionId, grant: prev.sesionId === sesionId ? prev.grant : null, dialogOpen: true }));
    }, [sesionId]);
    // ``closeDialog``/``reset`` son estables (no dependen de la caja): operan
    // sobre el estado almacenado y la derivación por caja hace el resto.
    const closeDialog = useCallback(() => {
        setState(prev => ({ ...prev, dialogOpen: false }));
    }, []);
    const install = useCallback((grant: OperadorPOSGrant | null) => {
        if (sesionId === null) return;
        setState(prev => ({ sesionId, grant, dialogOpen: prev.sesionId === sesionId ? prev.dialogOpen : false }));
    }, [sesionId]);
    const reset = useCallback(() => { setState(prev => EMPTY_GRANT_STATE(prev.sesionId)); }, []);
    return {
        grant: current.grant,
        token: current.grant?.token ?? null,
        dialogOpen: current.dialogOpen,
        openDialog,
        closeDialog,
        install,
        reset,
    };
};

interface OperadorGrantRowProps {
    grant: OperadorPOSGrant | null;
    disabled?: boolean;
    onIdentify: () => void;
}

/**
 * Fila compacta: operador identificado (o aviso) + botón para identificarse.
 * Envuelve (flex-wrap) y en móvil el botón ocupa todo el ancho (ver CSS).
 */
export const OperadorGrantRow: React.FC<OperadorGrantRowProps> = ({ grant, disabled = false, onIdentify }) => (
    <div className={`ogr-row${grant ? ' is-granted' : ''}`} data-testid="ogr-row">
        <span className="ogr-text">
            {grant ? `Operador de caja: ${grant.operador.nombre}` : 'Identifica al operador de caja con su PIN para continuar.'}
        </span>
        <button type="button" className="ogr-button" onClick={onIdentify} disabled={disabled}>
            {grant ? 'Cambiar operador' : 'Identificar operador'}
        </button>
    </div>
);

